/**
 * App-wide "open the impacted-sections panel for this document" seam.
 *
 * The panel needs the shell's run-start primitive, so it is mounted once by
 * the shell (``SourceImpactHost``). Entry points anywhere else in the tree —
 * the library's document detail, the chat's ``find_impacted_sections`` step —
 * call {@link openSourceImpact} without threading props through the shell.
 * A request made before a host is mounted is held and delivered on mount.
 */

import { useSyncExternalStore } from "react";

type Opener = (documentName: string) => void;

let host: Opener | null = null;
let pending: string | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Open the impacted-sections panel for a library document (by name). */
export function openSourceImpact(documentName: string): void {
  const name = documentName.trim();
  if (!name) return;
  if (host) host(name);
  else pending = name;
}

/** Register the mounted panel host; returns the unregister function. */
export function registerSourceImpactHost(opener: Opener): () => void {
  host = opener;
  notify();
  if (pending) {
    const name = pending;
    pending = null;
    opener(name);
  }
  return () => {
    if (host === opener) {
      host = null;
      notify();
    }
  };
}

/** True while a host is mounted (a run is open) — entry points hide otherwise. */
export function isSourceImpactAvailable(): boolean {
  return host !== null;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useSourceImpactAvailable(): boolean {
  return useSyncExternalStore(subscribe, isSourceImpactAvailable, isSourceImpactAvailable);
}

/** Test-only reset. */
export function __resetSourceImpactBusForTests(): void {
  host = null;
  pending = null;
  notify();
}
